<div class="cm-login-container">
    @if ($initializing()) {
        <div class="cm-login-card">
            <mat-progress-spinner mode="indeterminate" [diameter]="32" color="primary"></mat-progress-spinner>
        </div>
    } @else {
        <div class="cm-login-card" [class.cm-wide]="$currentStep() === 5">
            <div class="cm-login-card-header">
                <i class="far fa-sparkles cm-welcome-icon"></i>
                @if ($currentStep() === 2) {
                    Complete Your Profile
                } @else {
                    Welcome to colormass!
                }
            </div>

            <form class="cm-login-form" name="onboardingForm" [formGroup]="onboardingForm" novalidate>
                <!-- Step 1: Welcome -->
                @if ($currentStep() === 1) {
                    <div class="cm-step-content">
                        <h3 class="cm-step-title">Let's get you set up!</h3>
                        <div class="cm-welcome-features">
                            <div class="cm-feature-item">
                                <i class="far fa-arrow-to-bottom"></i>
                                <span>Download PBR materials and the Blender plugin</span>
                            </div>
                            <div class="cm-feature-item">
                                <i class="far fa-cube"></i>
                                <span>Create Configurators</span>
                            </div>
                            <div class="cm-feature-item">
                                <i class="far fa-grid"></i>
                                <span>Tile Textures</span>
                            </div>
                        </div>
                    </div>
                }

                <!-- Step 2: Complete Your Profile -->
                @if ($currentStep() === 2) {
                    <div class="cm-step-content">
                        <h3 class="cm-step-title">Tell us about yourself</h3>
                        <p class="cm-step-description">We need a few details to set up your account</p>

                        <cm-input-container
                            [matTooltip]="onboardingForm.controls.firstNameFormControl.hasError('required') ? 'First name is required' : ''"
                            [warning]="
                                onboardingForm.controls.firstNameFormControl.hasError('required') && onboardingForm.controls.firstNameFormControl.touched
                            "
                        >
                            <ng-container cm-top-label>
                                <div class="cm-top-label">First Name:</div>
                            </ng-container>
                            <ng-container cm-input>
                                <input
                                    placeholder="Enter first name"
                                    name="firstName"
                                    formControlName="firstNameFormControl"
                                    autofocus
                                    class="cm-first-name"
                                    (input)="$step2Error.set(null)"
                                />
                            </ng-container>
                        </cm-input-container>

                        <cm-input-container
                            [matTooltip]="onboardingForm.controls.lastNameFormControl.hasError('required') ? 'Last name is required' : ''"
                            [warning]="onboardingForm.controls.lastNameFormControl.hasError('required') && onboardingForm.controls.lastNameFormControl.touched"
                        >
                            <ng-container cm-top-label>
                                <div class="cm-top-label">Last Name:</div>
                            </ng-container>
                            <ng-container cm-input>
                                <input
                                    placeholder="Enter last name"
                                    name="lastName"
                                    formControlName="lastNameFormControl"
                                    class="cm-last-name"
                                    (input)="$step2Error.set(null)"
                                />
                            </ng-container>
                        </cm-input-container>

                        <cm-input-container>
                            <ng-container cm-top-label>
                                <div class="cm-top-label">Organization Name (optional):</div>
                            </ng-container>
                            <ng-container cm-input>
                                <input
                                    placeholder="Enter organization name"
                                    name="organizationName"
                                    formControlName="organizationNameFormControl"
                                    class="cm-org-name"
                                />
                            </ng-container>
                        </cm-input-container>

                        @if ($step2Error()) {
                            <div class="cm-field-error">{{ $step2Error() }}</div>
                        }
                    </div>
                }

                <!-- Step 3: Experience Question -->
                @if ($currentStep() === 3) {
                    <div class="cm-step-content">
                        <h3 class="cm-step-title">Tell us about your experience</h3>
                        <p class="cm-step-description">This helps us personalize your experience</p>

                        <div class="cm-experience-question">
                            <div class="cm-question-label">Do you have 3D experience?</div>
                            <cm-radio-group
                                [value]="onboardingForm.get('has3dExperienceFormControl')?.value"
                                (valueChange)="onboardingForm.get('has3dExperienceFormControl')?.setValue($event); $step3Error.set(null)"
                            >
                                <div class="cm-radio-buttons">
                                    <cm-radio-button [value]="true">Yes</cm-radio-button>
                                    <cm-radio-button [value]="false">No</cm-radio-button>
                                </div>
                            </cm-radio-group>
                        </div>
                        @if ($step3Error()) {
                            <div class="cm-field-error">{{ $step3Error() }}</div>
                        }
                    </div>
                }

                <!-- Step 4: Communication Preferences -->
                @if ($currentStep() === 4) {
                    <div class="cm-step-content">
                        <h3 class="cm-step-title">Stay updated</h3>
                        <p class="cm-step-description">Get the most out of colormass</p>

                        <div class="cm-newsletter-section">
                            <p class="cm-newsletter-title">Benefits</p>
                            <div class="cm-benefits-list">
                                <div class="cm-benefit-item">
                                    <i class="far fa-check-circle"></i>
                                    <span>New features and product updates</span>
                                </div>
                                <div class="cm-benefit-item">
                                    <i class="far fa-check-circle"></i>
                                    <span>Tips and best practices</span>
                                </div>
                                <div class="cm-benefit-item">
                                    <i class="far fa-check-circle"></i>
                                    <span>Free tutorials and resources</span>
                                </div>
                            </div>
                            <div class="cm-checkbox-container">
                                <label class="cm-checkbox-label">
                                    <input type="checkbox" formControlName="newsletterOptInFormControl" class="cm-checkbox" />
                                    <span class="cm-checkbox-text">Send me updates and tips</span>
                                </label>
                            </div>
                        </div>
                    </div>
                }

                <!-- Step 5: Plan Selection -->
                @if ($currentStep() === 5) {
                    <div class="cm-step-content">
                        <h3 class="cm-step-title">Choose your plan</h3>

                        <!-- Billing Interval Toggle: only meaningful for paid plans, so dimmed + inert while Free is
                             selected (opacity, not display, so the layout doesn't jump when the selection changes) -->
                        <div class="cm-billing-interval" [class.cm-disabled]="$isFreePlanSelected()" [attr.aria-disabled]="$isFreePlanSelected()">
                            <span class="cm-interval-label" [class.cm-active]="$billingInterval() === 'monthly'" (click)="$billingInterval.set('monthly')">
                                Monthly
                            </span>
                            <div
                                class="cm-interval-toggle"
                                [class.cm-active]="$billingInterval() === 'yearly'"
                                (click)="$billingInterval.set($billingInterval() === 'monthly' ? 'yearly' : 'monthly')"
                            >
                                <div class="cm-interval-toggle-slider" [class.cm-active]="$billingInterval() === 'yearly'"></div>
                            </div>
                            <span class="cm-interval-label" [class.cm-active]="$billingInterval() === 'yearly'" (click)="$billingInterval.set('yearly')">
                                Yearly
                                @if ($savingsPercentage(); as pct) {
                                    <span class="cm-save-badge">Save {{ pct }}%</span>
                                }
                            </span>
                        </div>

                        <!-- Plan Cards -->
                        <div class="cm-plan-selection">
                            <!-- Free Plan -->
                            <div class="cm-plan-card" [class.cm-selected]="$isFreePlanSelected()" (click)="$selectedPlanId.set(freePlanId)">
                                <div class="cm-plan-header">
                                    <h4 class="cm-plan-name">Free</h4>
                                    <div class="cm-plan-price">
                                        <span class="cm-price-amount">{{ $freePlanCurrencySymbol() }}0</span>
                                    </div>
                                </div>

                                <div class="cm-plan-trial">
                                    <span class="cm-plan-trial-days">No credit card required</span>
                                </div>

                                <div class="cm-plan-features">
                                    @for (feature of freePlanFeatures; track feature) {
                                        <div class="cm-plan-feature">
                                            <i class="far fa-check"></i>
                                            <span>{{ feature }}</span>
                                        </div>
                                    }
                                </div>
                            </div>

                            @for (plan of $subscriptionPlans(); track plan.productId) {
                                <div
                                    class="cm-plan-card"
                                    [class.cm-selected]="$selectedPlanId() === plan.productId"
                                    (click)="$selectedPlanId.set(plan.productId)"
                                >
                                    <div class="cm-plan-header">
                                        <h4 class="cm-plan-name">{{ plan.productName }}</h4>
                                        <div class="cm-plan-price">
                                            @if ($billingInterval() === "monthly") {
                                                <span class="cm-price-amount"
                                                    >{{ getCurrencySymbol(plan.monthlyPrice.currency) }}{{ (plan.monthlyPrice.unitAmount || 0) / 100 }}</span
                                                >
                                                <span class="cm-price-period">/month</span>
                                            } @else {
                                                <span class="cm-price-amount"
                                                    >{{ getCurrencySymbol(plan.yearlyPrice.currency) }}{{ (plan.yearlyPrice.unitAmount || 0) / 100 }}</span
                                                >
                                                <span class="cm-price-period">/year</span>
                                            }
                                        </div>
                                    </div>

                                    @if (plan.trialPeriodDays > 0) {
                                        <div class="cm-plan-trial">
                                            <span class="cm-plan-trial-days">{{ plan.trialPeriodDays }}-day free trial</span>
                                            <span class="cm-plan-trial-note">Cancel anytime before it ends.</span>
                                        </div>
                                    }

                                    @if (plan.productDescription) {
                                        <div class="cm-plan-description">{{ plan.productDescription }}</div>
                                    }

                                    <!-- Marketing Keypoints from Stripe -->
                                    @if (plan.features && plan.features.length > 0) {
                                        <div class="cm-plan-features">
                                            @for (feature of plan.features; track $index) {
                                                <div class="cm-plan-feature">
                                                    <i class="far fa-check"></i>
                                                    <span>{{ feature }}</span>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            }

                            <!-- Enterprise Plan -->
                            <div class="cm-plan-card cm-enterprise">
                                <div class="cm-plan-header">
                                    <h4 class="cm-plan-name">Enterprise</h4>
                                </div>

                                <div class="cm-plan-description">
                                    The ultimate version of the platform featuring advanced automation, high-performance tools and a lot more features. This
                                    plan is required for all organizations with annual revenue exceeding $2M
                                </div>

                                <!-- Enterprise Features -->
                                <div class="cm-plan-features">
                                    <div class="cm-plan-feature">
                                        <i class="far fa-check"></i>
                                        <span>Custom integrations</span>
                                    </div>
                                    <div class="cm-plan-feature">
                                        <i class="far fa-check"></i>
                                        <span>SLA & priority support</span>
                                    </div>
                                </div>

                                <a href="https://colormass.com/contact" target="_blank" rel="noopener noreferrer" class="cm-contact-button">
                                    <i class="far fa-envelope"></i>
                                    Contact Us
                                </a>
                            </div>
                        </div>
                    </div>
                }

                <!-- Navigation Buttons -->
                @if (!$showProgressCircle()) {
                    @if ($currentStep() === 5) {
                        <!-- Step 5: Plan Selection Actions -->
                        <div class="cm-step-5-buttons">
                            <button class="cm-back-button" type="button" (click)="previousStep()">
                                <i class="far fa-arrow-left"></i>
                                Back
                            </button>
                            <div class="cm-actions-container">
                                <button class="cm-sign-in-button cm-active" type="button" (click)="nextStep()">
                                    {{ $primaryActionLabel() }}
                                    <i class="far fa-arrow-right"></i>
                                </button>
                            </div>
                        </div>
                    } @else {
                        <!-- Steps 1-4: Regular Button Group -->
                        <div class="cm-button-group">
                            @if ($currentStep() > 1) {
                                <button class="cm-back-button" type="button" (click)="previousStep()">
                                    <i class="far fa-arrow-left"></i>
                                    Back
                                </button>
                            }

                            @if ($currentStep() === 1) {
                                <button class="cm-sign-in-button cm-active" type="button" (click)="nextStep()">
                                    Get Started
                                    <i class="far fa-arrow-right"></i>
                                </button>
                            }

                            @if ($currentStep() === 2) {
                                <button class="cm-sign-in-button cm-active" type="button" (click)="nextStep()">
                                    Continue
                                    <i class="far fa-arrow-right"></i>
                                </button>
                            }

                            @if ($currentStep() === 3) {
                                <button class="cm-sign-in-button cm-active" type="button" (click)="nextStep()">
                                    Next
                                    <i class="far fa-arrow-right"></i>
                                </button>
                            }

                            @if ($currentStep() === 4) {
                                <button class="cm-sign-in-button cm-active" type="button" (click)="nextStep()">
                                    Next
                                    <i class="far fa-arrow-right"></i>
                                </button>
                            }
                        </div>
                    }

                    <!-- Step Indicator -->
                    <div class="cm-step-indicator">
                        @for (step of [1, 2, 3, 4, 5]; track step) {
                            <div class="cm-step-dot" [class.cm-active]="$currentStep() === step" [class.cm-completed]="$currentStep() > step"></div>
                        }
                    </div>
                }
            </form>

            @if ($showProgressCircle()) {
                <mat-progress-spinner mode="indeterminate" [diameter]="24" color="primary"></mat-progress-spinner>
            }
        </div>
    }
</div>
